






































































































































import { createComponent, ref, inject, onMounted, watch, reactive, computed } from '@vue/composition-api'
import AppButton from '@/components/AppButton.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTextArea from '@/components/AppTextArea.vue'
import AppNavbar from '@/components/AppNavbar.vue'
import AppSelect from '@/components/AppSelect.vue'
import ImageUploadModal from '@/components/ImageUploadModal.vue'
import { UploadedImages, createFactory, updateFactory } from '../api'
import {
  FactoryPostData,
  FACTORY_TYPE,
  FactoryType,
  FactoryData,
  FactoryImage,
  FactoryStatusText
} from '../types'
import { MapFactoryController, initializeMinimap, getStatusBorderColor, getFactoryStatus } from '../lib/map'
import { MainMapControllerSymbol } from '../symbols'
import { useBackPressed } from '../lib/useBackPressed'
import { useGA } from '@/lib/useGA'
import { useModalState } from '../lib/hooks'
import { useAppState } from '../lib/appState'
import { useAlertState } from '../lib/useAlert'

export default createComponent({
  name: 'FormPage',
  components: {
    AppButton,
    AppTextField,
    AppTextArea,
    AppSelect,
    AppNavbar,
    ImageUploadModal
  },
  props: {
    factoryLocation: {
      type: Array,
      required: true
    },
    factoryData: {
      type: Object,
      required: true
    }
  },
  setup (props) {
    const { event } = useGA()
    const mapController = inject(MainMapControllerSymbol, ref<MapFactoryController>())
    let minimapController: MapFactoryController
    const [, modalActions] = useModalState()
    const [appState, { pageTransition }] = useAppState()
    const [, alertActions] = useAlertState()

    const onBack = () => {
      if (mapController.value) {
        mapController.value.mapInstance.setLUILayerVisible(false)
        pageTransition.closeFactoryPage()
      }
    }

    useBackPressed(onBack)

    const mode = computed(() => appState.isCreateMode ? 'create' : 'edit')

    const factoryTypeItems: Array<{ text: string, value: string }> = [
      { text: '請選擇工廠類型', value: '0' },
      ...FACTORY_TYPE
    ]

    const initialFactoryState = {
      name: '',
      others: '', // description
      type: '0' as FactoryType | '0',
      lng: 0,
      lat: 0,

      // image upload data
      images: [] as string[],
      imageUrls: [] as string[],
      nickname: '',
      contact: '',
      status: ''
    }

    // merge state
    if (appState.isEditMode) {
      const { factoryData } = props

      initialFactoryState.name = factoryData.name
      initialFactoryState.type = factoryData.factory_type
      initialFactoryState.others = factoryData.others
      initialFactoryState.imageUrls = (factoryData.images as FactoryImage[]).map(image => image.image_path)
      initialFactoryState.lng = factoryData.lng
      initialFactoryState.lat = factoryData.lat
      initialFactoryState.status = getFactoryStatus(factoryData as FactoryData)
    }

    const factoryFormState = reactive(initialFactoryState)

    const formPageState = reactive({
      imageUploadModalOpen: false,
      valid: false,
      submitting: false
    })

    const fieldSubmittingState = reactive({
      // eslint-disable-next-line @typescript-eslint/camelcase
      name_submitting: false,
      // eslint-disable-next-line @typescript-eslint/camelcase
      factory_type_submitting: false,
      // eslint-disable-next-line @typescript-eslint/camelcase
      others_submitting: false
    })

    watch(() => {
      const {
        images
      } = factoryFormState
      const imagesValid = images.length > 0

      formPageState.valid = imagesValid
    })

    const closeImageUploadModal = () => {
      formPageState.imageUploadModalOpen = false
      event('closeImageUploadModal')
    }
    const openImageUploadModal = () => {
      formPageState.imageUploadModalOpen = true
      event('openImageUploadModal')
    }

    const imagesToUpload = ref<FileList>([])
    const image = ref<HTMLElement>(null)

    const finishUploaderForm = (nickname: string, contact: string) => {
      factoryFormState.nickname = nickname
      factoryFormState.contact = contact
    }

    const updateFactoryFieldsFor = async (field: string, value: string) => {
      const { factoryData } = props
      if (!appState.isEditMode || !factoryData) {
        return
      }

      const updateKey = `${field}_submitting` as keyof typeof fieldSubmittingState

      try {
        fieldSubmittingState[updateKey] = true

        event('updateFactory', { field })
        const factory = await updateFactory(factoryData.id, {
          [field]: value
        })

        fieldSubmittingState[updateKey] = false

        if (mapController.value) {
          mapController.value.updateFactory(factoryData.id, factory)
          appState.factoryData = factory
        }
        modalActions.openUpdateFactorySuccessModal()
      } catch (err) {
        console.error(err)
      }
    }

    const minimap = ref<HTMLElement>(null)

    // !FIXME create factory minimap is set after pageState changed to CREATE_FACTORY_2
    watch(() => appState.pageState, () => {
      if (!appState.selectFactoryMode) {
        if (mapController.value) {
          const controller = mapController.value
          const center = controller.mapInstance.map.getView().getCenter() as number[]
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          minimapController = initializeMinimap(minimap.value!, center)
          minimapController.addFactories(controller.factories)

          if (appState.isCreateMode) {
            const [lng, lat] = props.factoryLocation as number[]
            minimapController.mapInstance.setMinimapPin(lng, lat)
          }
        }
      }
    })

    // !FIXME update factory minmimap is set after page mounted
    onMounted(() => {
      if (mapController.value) {
        const controller = mapController.value
        const center = controller.mapInstance.map.getView().getCenter() as number[]
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        minimapController = initializeMinimap(minimap.value!, center)
        minimapController.addFactories(controller.factories)

        if (appState.isEditMode) {
          const { factoryData } = props
          minimapController.mapInstance.setMinimapPin(factoryData.lng, factoryData.lat)
        }
      }
    })

    watch(() => props.factoryLocation, () => {
      if (appState.isCreateMode && minimapController) {
        const [lng, lat] = props.factoryLocation as number[]
        minimapController.mapInstance.setMinimapPin(lng, lat)
      }
    })

    return {
      minimap,
      onClickMinimap: () => {
        if (appState.isCreateMode && mapController.value) {
          mapController.value.mapInstance.setLUILayerVisible(true)
          pageTransition.gotoCreateStep(0)
        }
      },
      factoryFormState,
      formPageState,
      fieldSubmittingState,
      factoryTypeItems,
      containerStyle: {
        width: '100%'
      },
      openImageUploadModal,
      closeImageUploadModal,

      imagesToUpload,
      image, // image upload input ref,
      onClickImageUpload () {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        image.value!.click()
      },
      handleImagesUpload (e: Event) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        imagesToUpload.value = (e.target as HTMLInputElement).files!

        openImageUploadModal()
      },
      async finishImagesUpload (_images: UploadedImages | FactoryImage[], imageUrls: string[]) {
        if (appState.isCreateMode) {
          factoryFormState.imageUrls = imageUrls
          factoryFormState.images = (_images as UploadedImages).map(image => image.token)
        } else if (appState.isEditMode) {
          // TODO: refactor into sepearte method...
          try {
            const factory = { ...props.factoryData } as FactoryData
            const images = _images as FactoryImage[]

            factoryFormState.imageUrls = factoryFormState.imageUrls.concat(imageUrls)

            factory.images = factory.images.concat(images)

            if (mapController.value) {
              mapController.value.updateFactory(props.factoryData.id, factory)
              appState.factoryData = factory
            }
            modalActions.openUpdateFactorySuccessModal()
          } catch (err) {
            console.error(err)
          }
        }
      },
      onNavBack () {
        if (mapController.value) {
          onBack()
        }
      },
      async submitFactory () {
        if (!formPageState.valid) {
          alertActions.showAlert('您沒上傳工廠照片，\n請上傳至少一張照片以完成回報。')
          return
        }

        formPageState.submitting = true

        try {
          const [lng, lat] = props.factoryLocation as number[]
          const factory: FactoryPostData = {
            name: factoryFormState.name,
            others: factoryFormState.others,
            type: factoryFormState.type as FactoryType,
            lng,
            lat,
            images: factoryFormState.images,
            nickname: factoryFormState.nickname,
            contact: factoryFormState.contact
          }

          event('createFactory', { lng, lat })
          const resultFactory = await createFactory(factory)
          if (mapController.value) {
            mapController.value.addFactories([resultFactory])
          }
        } catch (e) {
          // TODO: handle create failure
        } finally {
          formPageState.submitting = false
        }

        // TODO: clear form

        pageTransition.closeFactoryPage()
      },
      finishUploaderForm,
      appState,
      mode,
      updateFactoryFieldsFor,

      getStatusBorderColor,
      FactoryStatusText,
      getFactoryStatus,
      modalActions
    }
  }
})
